import * as voicifyApi from '../../../../../shared/api';
import ApplicationModel from '../../../../../shared/models/applications/api/ApplicationModel';
import AppliedApplicationTemplateModel from '../../../../../shared/models/templating/api/AppliedApplicationTemplateModel';
import { getGoLiveWarnings } from '../../../../../shared/utils/goLiveWarningUtils';
import { confirmLaunchDateUpdate } from '../../../../../shared/utils/launchDateUpdate';
import { buildBulkLiveUpdateRequest } from './SparkManagementUpdateUtils';

interface BulkGoLiveResult {
    application?: ApplicationModel;
    warnings: string[];
    error?: string;
    skipped?: boolean;
}

export const updateBulkGoLiveApplication = async (
    app: ApplicationModel,
    value: { status: string, targetLaunchDate?: string },
    sharedTemplates: AppliedApplicationTemplateModel[] | null
): Promise<BulkGoLiveResult> => {
    if (app.allowsTemplating || ['shared configuration', 'shared topics'].includes(app.name?.trim().toLowerCase() ?? '')) {
        return { warnings: ['Shared Configuration was skipped because go-live status does not apply to it.'], skipped: true };
    }

    try {
        const result = await voicifyApi.updateApplication(app.id!, buildBulkLiveUpdateRequest(app, value));
        if (result.resultType !== 'Ok') {
            return { warnings: [], error: (result.errors || []).join(', ') || 'Unknown error' };
        }

        if (value.status === 'clear-target-date') {
            const confirmation = await confirmLaunchDateUpdate(
                result, '', false, () => voicifyApi.findApplication(app.id!), true
            );
            if (!confirmation.saved) {
                return { warnings: [], error: 'Target launch date is still set after clearing.' };
            }
            return {
                application: { ...app, ...confirmation.application, isLive: app.isLive, targetLaunchDate: null },
                warnings: []
            };
        }

        const application = { ...app, ...result.data, isLive: value.status === 'live' };
        if (value.status !== 'live') return { application, warnings: [] };

        if (!sharedTemplates) {
            return { application, warnings: ["Go-live warnings could not be checked. Review this location's Spark form."] };
        }

        try {
            const [templatesResult, telephonyResult] = await Promise.all([
                voicifyApi.getAppliedApplicationTemplatesForApp(app.id!),
                voicifyApi.getTelephonyDeployments(app.id!)
            ]);
            if (templatesResult.resultType !== 'Ok' || telephonyResult.resultType !== 'Ok') {
                return { application, warnings: ["Go-live warnings could not be checked. Review this location's Spark form."] };
            }
            return {
                application,
                warnings: getGoLiveWarnings(application, templatesResult.data || [], sharedTemplates, telephonyResult.data || [])
            };
        } catch {
            return { application, warnings: ["Go-live warnings could not be checked. Review this location's Spark form."] };
        }
    } catch {
        return { warnings: [], error: 'Request failed' };
    }
};
